import {
    ReactElement,
    useRef,
} from 'react';

import {
    Alert,
    Stack,
} from '@mantine/core';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
} from '@flexinet/ui-components';
import {DataTableColumn} from 'mantine-datatable';
import {useParams} from 'react-router-dom';

import {useApplicationProducts} from '../hooks/useApplicationProducts.ts';
import {useApplicationProductsSelection} from '../hooks/useApplicationProductsSelection.ts';
import {useApplicationProductsSearch} from '../hooks/useApplicationProductsSearch.ts';
import {useApplicationProductsTableColumns} from '../hooks/useApplicationProductsTableColumns.tsx';
import {
    ApplicationProduct,
    ApplicationProductStatus,
} from '../types';
import {rbacActions} from '../../../config/rbac/actions.ts';
import {useVerifiedPermissions} from '../../../hooks/useVerifiedPermissions.ts';
import classes from './RecordsTable.module.css';


export const RecordsTable = (
    {
        extraTools,
    }: {
        extraTools?: ReactElement[],
    } = {}
) => {


    const {id: applicationId} = useParams()
    if (!applicationId) {
        throw new Error('applicationId is required parameter')
    }


    const {allowedActions} = useVerifiedPermissions()

    const {
        // The full loaded set BEFORE keyword filtering — the window the
        // footer's match count is measured against.
        items,
        visibleItems: records,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingNextPage,
        nextToken,
        fetchNextPage,
    } = useApplicationProducts(applicationId);

    const {
        columns,
    } = useApplicationProductsTableColumns(applicationId);

    // Same module-scoped store the list hook filters on — this component owns
    // the input, useApplicationProducts owns the filtering.
    const {
        keyword,
        handleFilterByKeyword,
    } = useApplicationProductsSearch();

    const {
        selection,
        handleClearSelection,
        handleRowSelection,
    } = useApplicationProductsSelection();

    // Checkboxes appear only for an operator who can actually delete. Offering
    // selection to someone with no action to apply to it is a dead affordance.
    let selectedRecords = [] as ApplicationProduct[];
    if (allowedActions.includes(rbacActions.DeleteApplicationProduct)) {
        selectedRecords = records.filter(record => selection[record.id] === true);
    }

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // Two distinct empty states: nothing exists yet, versus nothing matched the
    // search. Collapsing them into one message tells an operator who mistyped a
    // keyword that this application has no products at all.
    const noRecordsText = keyword
        ? `No products match "${keyword}" in the records loaded so far.`
        : 'No products assigned to this application yet.';

    return (
        <Stack>
            {/*
              * TableSearchForm, not SimpleTableBatchAction: the latter REQUIRES
              * `limit` and `handleItemsPerPageChange` in its type, and a
              * token-paginated table cannot honour a page-size change — the
              * call site ends up passing a no-op handler, which is the tell.
              */}
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {extraTools}

            {isError && (
                <Alert color='red' title='Could not load application products'>
                    The API did not return the product list for this application. Try Reload;
                    if it persists the API may be unavailable in this environment.
                </Alert>
            )}

            <MantineDataTable
                columns={columns as DataTableColumn[]}
                records={records}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={(items) => {
                    handleClearSelection();
                    items.forEach(item => {
                        handleRowSelection((item as ApplicationProduct).id, true)
                    })
                }}
                // Sorting is client-side over the pages loaded so far — the same
                // scope the keyword filter works on. Token pagination cannot
                // reorder rows the browser has not fetched.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                noRecordsText={noRecordsText}
                onScrollToBottom={() => fetchNextPage()}
                scrollViewportRef={scrollViewportRef}
                rowClassName={(record) => {
                    if (record.applicationProductStatus === ApplicationProductStatus.ACTIVE) {
                        return classes.active;
                    } else if (record.applicationProductStatus === ApplicationProductStatus.INACTIVE) {
                        return classes.inactive;
                    }
                }}
                fetching={loading}
            />

            {/*
              * A SIBLING of the table, never a table prop. `loadedCount` is the
              * rows in the browser — there is no total under token pagination,
              * and the module writes no footer text of its own.
              */}
            <DataTableTokenPagination
                loadedCount={records.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                isFetchingMore={isFetchingNextPage}
                nextToken={nextToken}
                handleLoadMore={fetchNextPage}
                noun='product'
            />

        </Stack>
    );
};
